﻿import View from "./view.js";
import { withSubmitGuard } from "../submitGuard.js";
import { esc } from "../escape.js";
import {
  emojiPanelMarkup,
  visualHiddenInputsMarkup,
  wireVisualPicker,
  resetVisualPicker,
} from "../itemVisualPicker.js";

const FILE_UPLOAD_ZONE_MARKUP = `
  <label class="file-upload-label">
    <input type="file" name="image" accept="image/*" />
    <div class="file-upload-zone">
      <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/>
        <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
      </svg>
      <span class="file-upload-zone-main">Click to upload</span>
      <span class="file-upload-name">PNG or JPG (optional)</span>
    </div>
  </label>`;

class NewMenuItemView extends View {
  _parentElement = document.querySelector("#addMenuModal");
  _closeBtn = this._parentElement.querySelector(".modal-close");
  _modalDiv = document.querySelector(".modal-parent");
  _formParent = this._parentElement.querySelector(".add-menu-form");
  _selectOptionsElement = document.querySelector(".select-options");
  _visualField = document.getElementById("addItemVisualField");

  //variants
  _variantSection = document.querySelector(".variant-btn-section");
  _variantModal = document.getElementById("addVariantModal");
  _variantCheckBoxElement = document.getElementById("hasVariantsCheckbox");
  _showVariantBtn = document.getElementById("showVariantField");
  _addVariantElement = document.querySelector(".variant-modal");
  _addVariantOptionBtn = document.querySelector(".add-variant-option");
  _variantContainer = document.querySelector(".variant-options-field");
  _addVariantBtn = document.getElementById("addVariantSet");

  _addedVariants = [];
  _editingVariantIndex = null; // null = adding a new set; number = editing that index

  constructor() {
    super();

    this._insertVisualField();
    this._addVariantSet();
    this._showVariantModal();
    this._variantModalHide();
    this._addHandlerVariantActions();
    this._addFileUploadListener();
    wireVisualPicker(this._parentElement);
  }

  // Builds the Photo/Emoji toggle + both panels once, into the static
  // placeholder in index.html - shares markup with the Edit form via
  // itemVisualPicker.js so the two never drift.
  _insertVisualField() {
    this._visualField.insertAdjacentHTML(
      "beforeend",
      `<div class="item-photo-panel">${FILE_UPLOAD_ZONE_MARKUP}</div>
      ${emojiPanelMarkup({ selectedEmoji: "" })}
      ${visualHiddenInputsMarkup({ mode: "photo", selectedEmoji: "" })}`,
    );
  }

  _toggleModalClose() {
    this._closeBtn.addEventListener("click", (e) => {
      const overlay = this._parentElement;
      const inner = overlay.querySelector(".modal-content");
      if (inner) inner.classList.add("modal-exiting");
      setTimeout(() => {
        if (inner) inner.classList.remove("modal-exiting");
        overlay.classList.add("hidden");
        document.getElementById("newCategoryInput").classList.add("hidden");
        document.querySelector(".new-category-button").classList.add("hidden");
        this._formReset();
      }, 220);
    });
  }

  _toggleModalOpen() {
    this._modalDiv.addEventListener("click", function (e) {
      e.preventDefault();

      const btn = e.target.closest("#openAddModal");
      if (!btn) return;

      document.querySelector(".modal-overlay-form").classList.toggle("hidden");
    });
  }

  _uploadItem(handler) {
    this._formParent.addEventListener("submit", (e) => {
      e.preventDefault();
      //1.) Extract the data from fields
      const dataArr = [...new FormData(this._formParent)];
      const data = Object.fromEntries(dataArr);
      if (!data) return;
      // Guard the submit button: a rapid second submit would otherwise run after
      // _formReset() clears the inputs and upload a blank/garbage menu item.
      const btn = e.submitter ?? this._formParent.querySelector(".submit-btn");
      if (btn?.dataset.submitting === "1") return;
      withSubmitGuard(btn, () => handler(data), { busyText: "Adding…" });
      //2.) Refactor data to become model object
      //3.) Send data to controller
      //4.) close form modal
      //5.) Show success

      this._formReset();
    });
  }

  // NOTE: inventory tracking deliberately lives in the EDIT form only (owner
  // decision 2026-07-06) - the add form shows a static tip pointing there.

  _formReset() {
    document
      .querySelectorAll(".variant-options-container-text")
      .forEach((container) => container.remove());

    const inputs = document
      .getElementById("addMenuModal")
      .querySelectorAll("input, select, textarea");

    inputs.forEach((input) => {
      if (input.type === "checkbox" || input.type === "radio") {
        input.checked = false;
      } else {
        input.value = "";
      }
    });

    this._selectOptionsElement.value = "";
    document.getElementById("addMenuModal").classList.add("hidden");
    document.querySelector(".new-category-row").classList.add("hidden");
    this._showVariantBtn.classList.add("hidden");
    this._addedVariants = [];
    this._editingVariantIndex = null;
    this._addVariantBtn.textContent = "Add Variant";
    const fileNameSpan = this._parentElement.querySelector(".file-upload-name");
    fileNameSpan.textContent = "PNG or JPG (optional)";
    fileNameSpan.style.color = "";
    resetVisualPicker(this._parentElement);
  }

  _newMenuCategory() {
    const row = document.querySelector(".new-category-row");
    document
      .querySelector(".select-options")
      .addEventListener("change", function () {
        if (this.value === "new-category") {
          row.classList.remove("hidden");
          row.querySelector(".new-category-field").focus();
        } else {
          row.classList.add("hidden");
        }
      });
  }

  _addHandlerAddMenuCategory(handler) {
    const row = document.querySelector(".new-category-row");
    const field = document.getElementById("newCategoryInput");

    const submit = () => {
      const newCateg = field.value.trim();
      if (!newCateg) {
        alert("Please enter a category name");
        return;
      }
      handler(newCateg);
      field.value = "";
      row.classList.add("hidden");
    };

    document.querySelector(".new-category-button").addEventListener("click", submit);
    field.addEventListener("keydown", (e) => {
      if (e.key === "Enter") { e.preventDefault(); submit(); }
    });
  }

  _mapMenuCategoriesMarkUp(data) {
    this._selectOptionsElement.innerHTML = ``;

    this._selectOptionsElement.innerHTML = `
      <option class="hidden" value="Select category" disabled selected>Select category</option>
      <option value="new-category">Add new category</option>
    `;

    const markup = data.map(
      (i) => `<option value="${i}">${i[0].toUpperCase() + i.slice(1)}</option>
`,
    );
    this._selectOptionsElement.insertAdjacentHTML("afterbegin", markup);
  }

  _itemVariantsToggle() {
    this._variantCheckBoxElement.addEventListener("change", () => {
      if (this._variantCheckBoxElement.checked) {
        document.querySelector(".variants-section").classList.remove("hidden");
        this._showVariantBtn.classList.remove("hidden"); // ensure button is visible
      } else {
        document.querySelector(".variants-section").classList.add("hidden");
      }
    });
  }

  _showVariantModal() {
    this._showVariantBtn.addEventListener("click", (e) => {
      // Opening for a fresh add - clear any leftover values / edit state so the
      // modal never shows a previously edited or cancelled set.
      this._resetVariantFields();
      this._addVariantElement.classList.remove("hidden");
    });
  }

  _variantModalHide() {
    this._parentElement.addEventListener("click", (e) => {
      const btn = e.target.closest(".variant-close");
      if (!btn) return;
      const overlay = document.querySelector(".variant-modal");
      const inner = overlay.querySelector(".variant-content");
      if (inner) inner.classList.add("modal-exiting");
      setTimeout(() => {
        if (inner) inner.classList.remove("modal-exiting");
        overlay.classList.add("hidden");
      }, 220);
      // Cancel any in-progress edit; the original set stays in the list.
      this._editingVariantIndex = null;
      this._addVariantBtn.textContent = "Add Variant";
    });
  }

  // Clears the variant modal's inputs + any extra option rows.
  _resetVariantFields() {
    document
      .querySelectorAll(".variant-options-field.added-fields")
      .forEach((el) => el.remove());
    this._variantModal.querySelectorAll("input").forEach((input) => (input.value = ""));
  }

  _addVariantOption() {
    this._addVariantOptionBtn.addEventListener("click", () => this._appendOptionRow());
  }

  // Appends one option row to the variant modal, optionally pre-filled (used when
  // editing an existing set). Setting .value via JS avoids attribute escaping.
  _appendOptionRow(name = "", price = "") {
    const markup = `
      <div class="variant-options-field added-fields">
        <label>
          <input type="text" name="option-name" placeholder="e.g. Small, Medium, Large"/>
        </label>
        <label class="price-label">
          <input type="number" name="option-price" placeholder="$0.00" step="1" min="0"/>
        </label>
      </div>`;
    this._variantContainer.insertAdjacentHTML("beforeend", markup);
    const rows = document.querySelectorAll(".variant-options-field.added-fields");
    const row = rows[rows.length - 1];
    if (row) {
      row.querySelector('input[name="option-name"]').value = name ?? "";
      row.querySelector('input[name="option-price"]').value =
        price && price !== "0" ? price : "";
    }
  }

  // Loads an existing set back into the modal so it can be edited.
  _populateVariantModal(variant) {
    this._resetVariantFields();
    document.querySelector('input[name="variant-name"]').value = variant.optionLabel ?? "";
    const opts = variant.options?.length
      ? variant.options
      : [{ optionName: "", optionPrice: "" }];
    const firstName = this._variantContainer.querySelector('input[name="option-name"]');
    const firstPrice = this._variantContainer.querySelector('input[name="option-price"]');
    if (firstName) firstName.value = opts[0].optionName ?? "";
    if (firstPrice)
      firstPrice.value =
        opts[0].optionPrice && opts[0].optionPrice !== "0" ? opts[0].optionPrice : "";
    for (let i = 1; i < opts.length; i++)
      this._appendOptionRow(opts[i].optionName, opts[i].optionPrice);
  }

  _addVariantSet() {
    this._addVariantBtn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      const optionLabel = document
        .querySelector('input[name="variant-name"]')
        .value.trim();

      if (!optionLabel) {
        alert("Please insert an option name or label");
        return;
      }

      const optionRows = document.querySelectorAll('input[name="option-name"]');
      const options = Array.from(optionRows).map((optionInput) => {
        const optionRow = optionInput.closest(".variant-options-field");
        const priceInput = optionRow.querySelector('input[name="option-price"]');
        return {
          optionName: optionInput.value.trim(),
          optionPrice: priceInput.value !== "" ? priceInput.value : "0",
        };
      });

      const set = { optionLabel, options };
      if (this._editingVariantIndex !== null) {
        this._addedVariants[this._editingVariantIndex] = set; // edit in place
      } else {
        this._addedVariants.push(set);
      }

      this._renderAddedVariants();

      // Clear + close the modal, reset edit state.
      this._resetVariantFields();
      this._editingVariantIndex = null;
      this._addVariantBtn.textContent = "Add Variant";
      document.querySelector(".variant-modal").classList.add("hidden");
    });
  }

  // Rebuilds the summary of added variant sets, each with Edit + Remove controls.
  _renderAddedVariants() {
    document
      .querySelectorAll(".variant-options-container-text")
      .forEach((el) => el.remove());

    const markup = this._addedVariants
      .map((variant, idx) => {
        const optionsHTML = variant.options
          .map(
            (opt) =>
              `<div class="option-pair">
                 <span class="option-name">${esc(opt.optionName)}</span>
                 <span class="option-price">${esc(opt.optionPrice)}</span>
               </div>`,
          )
          .join("");

        return `<div class="variant-options-container-text" data-variant-index="${idx}">
            <div class="variant-set-head">
              <div class="option-label">${esc(variant.optionLabel)}</div>
              <div class="variant-set-actions">
                <button type="button" class="variant-edit-btn" data-variant-index="${idx}">Edit</button>
                <button type="button" class="variant-remove-btn" data-variant-index="${idx}" aria-label="Remove variant set">&times;</button>
              </div>
            </div>
            <div class="option-pairs">${optionsHTML}</div>
          </div>`;
      })
      .join("");

    this._variantSection.insertAdjacentHTML("afterend", markup);
  }

  // Delegated Edit / Remove for each added variant set.
  _addHandlerVariantActions() {
    this._parentElement.addEventListener("click", (e) => {
      const removeBtn = e.target.closest(".variant-remove-btn");
      if (removeBtn) {
        const idx = Number(removeBtn.dataset.variantIndex);
        this._addedVariants.splice(idx, 1);
        if (this._editingVariantIndex === idx) this._editingVariantIndex = null;
        this._renderAddedVariants();
        return;
      }
      const editBtn = e.target.closest(".variant-edit-btn");
      if (editBtn) {
        const idx = Number(editBtn.dataset.variantIndex);
        const variant = this._addedVariants[idx];
        if (!variant) return;
        this._editingVariantIndex = idx;
        this._populateVariantModal(variant);
        this._addVariantBtn.textContent = "Save Variant";
        this._addVariantElement.classList.remove("hidden");
        return;
      }
    });
  }

  _addFileUploadListener() {
    const fileInput = this._parentElement.querySelector(
      'input[type="file"][name="image"]',
    );
    const fileNameSpan = this._parentElement.querySelector(".file-upload-name");

    fileInput.addEventListener("change", () => {
      const file = fileInput.files?.[0];
      const zone = this._parentElement.querySelector(".file-upload-zone");
      if (!file) {
        fileNameSpan.textContent = "PNG or JPG (optional)";
        fileNameSpan.style.color = "";
        zone?.classList.remove("file-upload-zone--selected");
        return;
      }
      if (!file.type.startsWith("image/")) {
        fileInput.value = "";
        fileNameSpan.textContent = "Please select an image file";
        fileNameSpan.style.color = "var(--color-danger, #ef4444)";
        return;
      }
      fileNameSpan.textContent = "✓ " + file.name;
      fileNameSpan.style.color = "";
      zone?.classList.add("file-upload-zone--selected");
    });
  }
}

export default new NewMenuItemView();
